import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { REFERENCE_PROJECTS } from "../config/referenceProjects";
const AUTO_INTERVAL = 8000;

export default function ReferenceGallery() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const total = REFERENCE_PROJECTS.length;

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % total), AUTO_INTERVAL);
    return () => clearTimeout(timerRef.current);
  }, [idx, paused, total]);

  const go = (n) => setIdx((i) => (i + n + total) % total);
  const active = REFERENCE_PROJECTS[idx];

  return (
    <section
      className="site-container section-block"
      data-testid="reference-gallery"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mb-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-6 reveal">
        <div>
          <div className="eyebrow mb-4 text-[#5FA637]">Projekte</div>
          <h2 className="section-title max-w-3xl">
            Umsetzungen in <span className="italic-accent">Bildern.</span>
          </h2>
          <p className="mt-3 text-xs text-white/40 italic">
            * Beispiel-Visualisierungen — werden durch Fotos freigegebener Projekte ersetzt.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-display text-lg tabular-nums text-white/50" data-testid="gallery-counter">
            <span className="text-white">{String(idx + 1).padStart(2, "0")}</span>
            <span className="mx-2 text-white/30">/</span>
            {String(total).padStart(2, "0")}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => go(-1)} data-testid="gallery-prev" aria-label="Vorheriges Projekt"
              className="w-11 h-11 border border-white/20 hover:border-[#5FA637] hover:text-[#5FA637] flex items-center justify-center transition-colors">
              <ChevronLeft size={16}/>
            </button>
            <button onClick={() => go(1)} data-testid="gallery-next" aria-label="Nächstes Projekt"
              className="w-11 h-11 border border-white/20 hover:border-[#5FA637] hover:text-[#5FA637] flex items-center justify-center transition-colors">
              <ChevronRight size={16}/>
            </button>
          </div>
        </div>
      </div>

      {/* Main showcase */}
      <div className="grid grid-cols-1 lg:grid-cols-12 border border-white/10" data-testid="gallery-card">
        {/* Image → clickable to detail */}
        <Link to={`/projekte/${active.slug}`} className="lg:col-span-8 relative aspect-[16/10] lg:aspect-auto bg-[#0A1F38] overflow-hidden group" data-testid="gallery-image-link">
          <img
            key={idx}
            src={active.image}
            alt={active.title}
            className="absolute inset-0 w-full h-full object-cover animate-fade-in group-hover:scale-[1.03] transition-transform duration-700"
            loading="lazy"
            data-testid="gallery-image"
          />
          <div className="absolute top-5 left-5 bg-[#5FA637] text-[#0A1A2E] px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold" data-testid="gallery-phase">
            {active.phase}
          </div>
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0A1F38]/70 to-transparent pointer-events-none"/>
        </Link>

        {/* Details */}
        <div className="lg:col-span-4 p-7 md:p-9 bg-[#112640] flex flex-col justify-between" data-testid="gallery-details">
          <div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-4 animate-fade-up" data-testid="gallery-title">
              {active.title}
            </h3>
            <p className="text-white/75 text-sm md:text-base leading-relaxed mb-8 animate-fade-up">
              {active.subtitle}
            </p>

            {/* Meta grid */}
            {active.meta?.length > 0 && (
              <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-8">
                {active.meta.map((m, i) => (
                  <div key={i} className="bg-[#112640] p-4">
                    <div className="eyebrow mb-1">{m.label}</div>
                    <div className="text-sm text-white/90 font-medium">{m.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Tags */}
            {active.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-8">
                {active.tags.map((tag, i) => (
                  <span key={i} className="text-[10px] uppercase tracking-[0.12em] px-2.5 py-1 bg-white/5 border border-white/15 text-white/70">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <Link to={`/projekte/${active.slug}`} className="btn-ghost self-start" data-testid="gallery-inquiry">
            Projekt-Steckbrief ansehen <ArrowUpRight size={14}/>
          </Link>
        </div>
      </div>

      {/* Thumbnail strip */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="gallery-thumbs">
        {REFERENCE_PROJECTS.map((p, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            data-testid={`gallery-thumb-${i}`}
            className={`relative aspect-[16/10] overflow-hidden border transition-all ${
              i === idx ? "border-[#5FA637] opacity-100" : "border-white/10 opacity-55 hover:opacity-90 hover:border-white/30"
            }`}
          >
            <img src={p.image} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
            <div className="absolute bottom-1 left-2 right-2 text-[9px] uppercase tracking-wider text-white/90 font-semibold truncate">
              {p.phase}
            </div>
            {i === idx && <div className="absolute inset-0 ring-2 ring-[#5FA637] pointer-events-none"/>}
          </button>
        ))}
      </div>

      <style>{`
        @keyframes gallery-fade-in {
          from { opacity: 0; transform: scale(1.03); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in { animation: gallery-fade-in 0.7s cubic-bezier(.16,1,.3,1); }
        @keyframes gallery-fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: gallery-fade-up 0.5s cubic-bezier(.16,1,.3,1); }
      `}</style>
    </section>
  );
}
